import { BehaviorSubject, Subscription } from 'rxjs';
import { SOLACE_SUBSCRIPTIONS } from 'src/app';
import { PositionItem } from '@models/iPositionItem.class';
import { PositionSolaceService } from 'src/core';
import { OrderByPipe } from 'src/shared/pipes/';
import {
  MarketType,
  SharedSolaceMainService,
  SharedSolaceStreamService,
} from 'src/solace/';

export class PositionData {
  dataChange: BehaviorSubject<PositionItem[]> = new BehaviorSubject<
    PositionItem[]
  >([]);
  get data(): PositionItem[] {
    return this.dataChange.value;
  }

  public positionSolaceService: PositionSolaceService =
    new PositionSolaceService();

  protected symbolSubscription: Subscription;
  private sort: any;
  private orderByPipe: OrderByPipe = new OrderByPipe();
  private solaceItems: any[] = [];
  private sharedSolaceStreamService: SharedSolaceStreamService;
  private sharedSolaceMainService: SharedSolaceMainService;

  setData(
    origin: any[],
    sharedSolaceStreamService?: SharedSolaceStreamService,
    sharedSolaceMainService?: SharedSolaceMainService,
    isNeededSuscribeSolace?: boolean,
  ): void {
    const newData: PositionItem[] = [];
    let indexDataPosition = 0;
    for (const item of origin) {
      if (item.issueId !== 'Subtotal') {
        newData.push(
          this.createPositionItem(
            item,
            sharedSolaceStreamService,
            sharedSolaceMainService,
            isNeededSuscribeSolace,
            indexDataPosition,
          ),
        );
        indexDataPosition++;
      }
    }

    if (this.sort) {
      this.dataChange.next(this.orderByPipe.transform(this.data, this.sort));
    } else {
      this.dataChange.next(newData);
    }
  }

  setSorting(sorting: any): void {
    this.sort = sorting;
    this.dataChange.next(this.orderByPipe.transform(this.data, this.sort));
  }

  /**
   * Unsubscribe all solace streams and remove consumer from observable.
   */
  disposeAll() {
    this.unsubscribeSolace();
  }

  /**
   * Calculates daily variation
   * If valueType === 26 is a "Venta en corto" and needs to change the sign
   * @returns dailyVariation
   */
  calculateDailyVariation(
    marketValue: number,
    closePrice: number,
    valueType: number,
    shares: number,
  ) {
    const closeMarketValue = closePrice * shares;
    let dailyVariation =
      closePrice !== 0 ? marketValue / closeMarketValue - 1 : 0;

    if (valueType === 26) {
      dailyVariation =
        Math.sign(dailyVariation) === -1
          ? Math.abs(dailyVariation)
          : -Math.abs(dailyVariation);
    }
    return parseFloat(dailyVariation.toFixed(4));
  }

  calculateHistoricalVariation(
    marketValue: number,
    averageCost: number,
    valueType: number,
  ) {
    let dailyVariation = averageCost !== 0 ? marketValue / averageCost - 1 : 0;

    if (valueType === 26) {
      dailyVariation =
        Math.sign(dailyVariation) === -1
          ? Math.abs(dailyVariation)
          : -Math.abs(dailyVariation);
    }

    return parseFloat(dailyVariation.toFixed(4));
  }

  /**
   * Normalize item position
   */
  private createPositionItem(
    item: any,
    sharedSolaceStreamService: SharedSolaceStreamService,
    sharedSolaceMainService: SharedSolaceMainService,
    isNeededSuscribeSolace: boolean,
    indexPosition?: number,
  ): PositionItem {
    const { position, solaceSymbol } = item;
    let marketName = '';
    let marketObservable$: any;

    // console.log('createPositionItem: position %o    --> item: %o    ---> solaceSymbol: %o', position, item, solaceSymbol);
    if (isNeededSuscribeSolace) {
      if (
        item &&
        (item.instrumentType === MarketType.BMV ||
          item.instrumentType === MarketType.SIC)
      ) {
        marketName =
          item && item.instrumentType === MarketType.BMV
            ? 'marketBMV'
            : 'marketSIC';
        this.sharedSolaceStreamService = sharedSolaceStreamService;
        this.sharedSolaceMainService = sharedSolaceMainService;

        this.subscribeSolace(item.issueId, marketName);

        marketObservable$ = this.sharedSolaceStreamService.getObservableSymbol(
          marketName,
          item.issueId,
          'portfolio',
        );
      } else {
        marketName = '';
      }
    }

    let solacePrice = 0;
    let priceForMarketValue = 0;

    const ppp = item.weightedAveragePrice; // solaceItem ? solaceItem.ppp : 0;
    const marketPrice = item.lastPrice;
    const valueType = item.positionValueType;
    const averageCost = parseFloat(item.averagePrice.toFixed(4));
    const costAmount = parseFloat(item.averageCost.toFixed(4));
    const plusMinus = item.yieldValue;
    let lastSolacePrice = 0;
    let solaceMarketPrice = solacePrice;

    if (marketName.length < 3) {
      // position.instrument.closePrice = position.instrument.lastPrice;
      solaceMarketPrice = item.lastPrice;
    }

    const positionItem: any = {
      issueId: item.issueId,
      indexPosition,
      shares: item.quantity,
      averageCost,
      costAmount,
      changeAvg: item.changeAvgSymbol,
      marketPrice,
      closePrice: item.closePrice,
      marketValue: item.marketValue,
      plusMinus,
      ppp,
      var: item.historicalVariationPercentage,
      varD: item.dailyVariationPercentage,
      portfolio: item.positionPercentage,
      valueType,
      solaceMarketPrice,
      lastPrice: item.lastPrice,
    };

    if (marketName.length > 3) {
      this.symbolSubscription = marketObservable$.subscribe((symbol: any) => {
        if (symbol !== undefined) {
          solacePrice = symbol.hechos.body.lastPrice;
          positionItem.solaceMarketPrice = solacePrice;
          positionItem.ppp =
            symbol.hechos.body.bmvWeightedAvgPrice > 0
              ? symbol.hechos.body.bmvWeightedAvgPrice
              : symbol.hechos.body.weightedAveragePrice;
          priceForMarketValue =
            symbol.hechos.body.bmvWeightedAvgPrice > 0
              ? symbol.hechos.body.bmvWeightedAvgPrice
              : solacePrice;
          positionItem.marketValue = priceForMarketValue * positionItem.shares;
          positionItem.plusMinus =
            positionItem.shares * priceForMarketValue - positionItem.costAmount;
          positionItem.varD = symbol.hechos.body.variationPercentage / 100;
          positionItem.var = this.calculateHistoricalVariation(
            positionItem.marketValue,
            positionItem.costAmount,
            positionItem.valueType,
          );
          if (lastSolacePrice !== solacePrice) {
            this.positionSolaceService.changeOption(positionItem);
          }
          lastSolacePrice = solacePrice;
        }
      });

      const solaceItem = {
        symbol: item.issueId,
        marketName,
      };

      this.solaceItems.push(solaceItem);
    }

    return positionItem;
  }

  private subscribeSolace(issue: string, marketName: string) {
    const topicsSubscribe =
      marketName === 'marketBMV'
        ? SOLACE_SUBSCRIPTIONS.MONITOR_BMV
        : SOLACE_SUBSCRIPTIONS.MONITOR_SIC;
    const topicsAggSubscribe =
      marketName === 'marketBMV'
        ? SOLACE_SUBSCRIPTIONS.MONITOR_BMV_VOL
        : SOLACE_SUBSCRIPTIONS.MONITOR_SIC_VOL;
    this.sharedSolaceMainService.subscribeTopic(topicsSubscribe, issue);
    this.sharedSolaceMainService.subscribeTopic(topicsAggSubscribe, issue);
  }

  private unsubscribeSolace() {
    if (this.solaceItems.length > 0) {
      this.solaceItems.forEach((item: any) => {
        const symbol = item.symbol;
        const marketName = item.marketName;

        this.sharedSolaceStreamService.removeConsumerFromObservable(
          symbol,
          marketName,
          'portfolio',
        );

        const consumerFromObservable$: any =
          this.sharedSolaceStreamService.getConsumerFromObservable(
            symbol,
            marketName,
            'porfolio',
          );
        const consumers = consumerFromObservable$
          ? consumerFromObservable$.length
          : 0;
        // console.log('symbol: %o   consumers: %o', symbol, consumers);
        if (consumers === 0) {
          const topicsSubscribe =
            marketName === 'marketBMV'
              ? SOLACE_SUBSCRIPTIONS.MONITOR_BMV
              : SOLACE_SUBSCRIPTIONS.MONITOR_SIC;
          const topicsAggSubscribe =
            marketName === 'marketBMV'
              ? SOLACE_SUBSCRIPTIONS.MONITOR_BMV_VOL
              : SOLACE_SUBSCRIPTIONS.MONITOR_SIC_VOL;

          this.sharedSolaceMainService.unSubscribeTopic(
            topicsSubscribe,
            symbol,
          );
          this.sharedSolaceMainService.unSubscribeTopic(
            topicsAggSubscribe,
            symbol,
          );
        }
      });
    }

    if (this.symbolSubscription) {
      this.symbolSubscription.unsubscribe();
    }
    this.solaceItems = [];
  }
}
